import { useState, useCallback } from 'react';
import { QUICK_RANGES, QuickRange } from '../types';
import { fmtDate, toLocalDatetimeStr } from '../utils';

export function useTimeRange() {
  const [timeRangeLabel, setTimeRangeLabel] = useState('Last 5 minutes');
  const [activeRangeMs, setActiveRangeMs] = useState(5 * 60 * 1000);
  const [absoluteFrom, setAbsoluteFrom] = useState<number>(Date.now() - 5 * 60 * 1000);
  const [absoluteTo, setAbsoluteTo] = useState<number>(Date.now());
  const [isRelative, setIsRelative] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const getTimeRangeMs = useCallback((): { start: number; end: number } => {
    if (isRelative) {
      const now = Date.now();
      return { start: now - activeRangeMs, end: now };
    }
    return { start: absoluteFrom, end: absoluteTo };
  }, [isRelative, activeRangeMs, absoluteFrom, absoluteTo]);

  const handleTimePickerOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleQuickRangeSelect = useCallback((range: QuickRange) => {
    setTimeRangeLabel(range.label);
    setActiveRangeMs(range.ms);
    setIsRelative(true);
    const now = Date.now();
    setAbsoluteFrom(now - range.ms);
    setAbsoluteTo(now);
    setAnchorEl(null);
  }, []);

  const handleApplyCustomRange = useCallback((fromMs: number, toMs: number, label: string) => {
    setAbsoluteFrom(fromMs);
    setAbsoluteTo(toMs);
    setActiveRangeMs(toMs - fromMs);
    setIsRelative(false);
    setTimeRangeLabel(label);
    setAnchorEl(null);
  }, []);

  const shiftTime = useCallback((direction: 'back' | 'forward') => {
    const shiftMs = direction === 'back' ? -activeRangeMs : activeRangeMs;
    const now = Date.now();

    let currentFrom: number, currentTo: number;
    if (isRelative) {
      currentTo = now;
      currentFrom = now - activeRangeMs;
    } else {
      currentFrom = absoluteFrom;
      currentTo = absoluteTo;
    }

    let newFrom = currentFrom + shiftMs;
    let newTo = currentTo + shiftMs;

    if (direction === 'forward' && newTo > now) {
      newTo = now;
      newFrom = now - activeRangeMs;
      const quickRange = QUICK_RANGES.find(r => r.ms === activeRangeMs);
      if (quickRange) {
        setTimeRangeLabel(quickRange.label);
        setIsRelative(true);
      } else {
        setTimeRangeLabel(`${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`);
        setIsRelative(false);
      }
      setAbsoluteFrom(newFrom);
      setAbsoluteTo(newTo);
      return;
    }

    setAbsoluteFrom(newFrom);
    setAbsoluteTo(newTo);
    setIsRelative(false);
    setTimeRangeLabel(`${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`);
  }, [activeRangeMs, isRelative, absoluteFrom, absoluteTo]);

  const handleZoomOut = useCallback(() => {
    const quickRange = QUICK_RANGES.find(r => r.label === timeRangeLabel);
    if (quickRange) {
      const idx = QUICK_RANGES.indexOf(quickRange);
      if (idx < QUICK_RANGES.length - 1) {
        handleQuickRangeSelect(QUICK_RANGES[idx + 1]);
      }
    }
  }, [timeRangeLabel, handleQuickRangeSelect]);

  const handleZoomIn = useCallback(() => {
    const quickRange = QUICK_RANGES.find(r => r.label === timeRangeLabel);
    if (quickRange) {
      const idx = QUICK_RANGES.indexOf(quickRange);
      if (idx > 0) {
        handleQuickRangeSelect(QUICK_RANGES[idx - 1]);
      }
    } else {
      const closestIdx = QUICK_RANGES.findIndex(r => r.ms >= activeRangeMs);
      const targetIdx = closestIdx > 0 ? closestIdx - 1 : 0;
      handleQuickRangeSelect(QUICK_RANGES[targetIdx]);
    }
  }, [timeRangeLabel, activeRangeMs, handleQuickRangeSelect]);

  const getPickerInitialDates = useCallback(() => {
    const now = new Date();
    const fromDate = isRelative ? new Date(now.getTime() - activeRangeMs) : new Date(absoluteFrom);
    const toDate = isRelative ? now : new Date(absoluteTo);
    return {
      from: toLocalDatetimeStr(fromDate),
      to: toLocalDatetimeStr(toDate),
    };
  }, [isRelative, activeRangeMs, absoluteFrom, absoluteTo]);

  const tzOffset = new Date().getTimezoneOffset();
  const tzSign = tzOffset <= 0 ? '+' : '-';
  const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
  const tzMins = String(Math.abs(tzOffset) % 60).padStart(2, '0');
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzString = `UTC${tzSign}${tzHours}:${tzMins}`;

  // Stable signature of the current selection. Changes only when the user picks
  // a different range, so consumers (e.g. the label dialog) can use it to detect
  // when to refetch. Relative ranges are keyed by their span so "Last 5 minutes"
  // doesn't continuously change as `now` advances.
  const timeRangeKey = isRelative
    ? `rel:${activeRangeMs}`
    : `abs:${absoluteFrom}-${absoluteTo}`;

  return {
    timeRangeLabel,
    timeRangeKey,
    anchorEl,
    setAnchorEl,
    getTimeRangeMs,
    handleTimePickerOpen,
    handleQuickRangeSelect,
    handleApplyCustomRange,
    shiftTime,
    handleZoomOut,
    handleZoomIn,
    getPickerInitialDates,
    tzName,
    tzString,
  };
}
